import { LoiForm } from './loi-form';
import { EmployeeConsiderationPayer } from './employee-consideration-payer.enum';
import {
    DeferredVariablePaymentType,
    CorporateEquityType,
} from './term-form';
import { hasValueForLinkedField } from './has-value-for-linked-field';
import { isLegalIssueVisibleByLogic } from './legal-issue-visibility';

export interface LoiFormErrors {
    termFormFields: Set<string>;
    legalIssueIds: Set<string>;
    additionalCovenantIds: Set<string>;
    loiFileRequired?: boolean;
}

export interface LoiFormValidationOptions {
    documentDetailsId?: string;
    isFsb?: boolean;
    skipLegalTabs?: boolean;
    isAdmin?: boolean;
}

function isNonEmpty(value: string | number | null | undefined): boolean {
    return value !== null && value !== undefined && String(value).trim() !== '';
}

function isNonNull(value: number | null | undefined): boolean {
    return value !== null && value !== undefined;
}

export function validateLoiForm(formData: LoiForm, options?: LoiFormValidationOptions): LoiFormErrors {
    const termFormFields = new Set<string>();
    const legalIssueIds = new Set<string>();
    const additionalCovenantIds = new Set<string>();
    const loiFileRequired = !options?.documentDetailsId && !options?.isAdmin;

    const termForm = formData.termForm;
    if (termForm) {
        if (!isNonEmpty(termForm.loiExpirationDate)) termFormFields.add('loiExpirationDate');
        if (!isNonNull(termForm.exclusivityPeriodDays)) termFormFields.add('exclusivityPeriodDays');

        if (!isNonEmpty(termForm.propertyPurchased?.practicePropertyPurchased)) termFormFields.add('practicePropertyPurchased');
        if (!isNonEmpty(termForm.propertyPurchased?.practiceRealPropertyInclude)) termFormFields.add('practiceRealPropertyInclude');

        const summary = termForm.purchasePriceSummary;
        const cashAtCloser = summary?.cashAtCloser;
        if (!isNonNull(cashAtCloser?.cashAtClose)) termFormFields.add('cashAtClose');
        if (!isNonNull(cashAtCloser?.workingCapitalHoldback?.value)) termFormFields.add('workingCapitalHoldbackValue');
        const wchValue = Number(cashAtCloser?.workingCapitalHoldback?.value) || 0;
        if (wchValue > 0 && !isNonNull(cashAtCloser?.workingCapitalHoldback?.monthsHold)) {
            termFormFields.add('workingCapitalHoldbackMonths');
        }


        const dfpRows = summary?.deferredFixedPayments?.rows ?? [];
        if (!dfpRows.some((r) => isNonNull(r.amount))) termFormFields.add('deferredFixedPayments');

        const dvpRows = summary?.deferredVariablePayments?.rows ?? [];
        const dvpEarnout = dvpRows.find((r) => r.type === DeferredVariablePaymentType.Earnout);
        const dvpSynthetic = dvpRows.find((r) => r.type === DeferredVariablePaymentType.Synthetic);
        if (!dvpRows.some((r) => r.type === DeferredVariablePaymentType.Earnout && isNonNull(r.valueAtClosing))) termFormFields.add('earnoutValueAtClosing');
        if (!dvpRows.some((r) => r.type === DeferredVariablePaymentType.Synthetic && isNonNull(r.valueAtClosing))) termFormFields.add('syntheticJvEquityValueAtClosing');

        if (!isNonNull(summary?.jvEquity?.row?.valueAtClosing)) termFormFields.add('jvEquityValueAtClosing');

        const corpRows = summary?.corporateEquity?.rows ?? [];
        const corpCommon = corpRows.find((r) => r.type === CorporateEquityType.Common);
        const corpPreferred = corpRows.find((r) => r.type === CorporateEquityType.Preferred);
        if (!isNonNull(corpCommon?.valueAtClosing)) termFormFields.add('corporateEquityCommonValueAtClosing');
        if (!isNonNull(corpPreferred?.valueAtClosing)) termFormFields.add('corporateEquityPreferredValueAtClosing');
        if (!isNonNull(summary?.other)) termFormFields.add('other');

        const dfpTotal = dfpRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
        const dvpTotal = dvpRows.reduce((s, r) => s + (Number(r.valueAtClosing) || 0), 0);

        if ((dfpTotal > 0 || dvpTotal > 0) && !options?.isAdmin) {
            const gdpi = termForm.generalDeferredPaymentsInformation;
            if (!isNonEmpty(gdpi?.obligorName)) termFormFields.add('obligorName');
            // The financial fields may stay empty only when the buyer explains in the section comment instead.
            const obligorSectionComment = (termForm.fieldComments ?? []).find(
                (c) => c.fieldKey === 'generalDeferredPaymentsInformation'
            )?.buyerComment;
            if (!isNonEmpty(obligorSectionComment)) {
                if (!isNonEmpty(gdpi?.seniorDebt)) termFormFields.add('seniorDebt');
                if (!isNonEmpty(gdpi?.allOtherLiabilities)) termFormFields.add('allOtherLiabilities');
                if (!isNonEmpty(gdpi?.balanceSheetCash)) termFormFields.add('balanceSheetCash');
                if (!isNonEmpty(gdpi?.ltmEbitdaProForma)) termFormFields.add('ltmEbitdaProForma');
            }
        }

        if (termForm.employeeConsiderationPayer === null || termForm.employeeConsiderationPayer === undefined) {
            termFormFields.add('employeeConsiderationPayer');
        }

        const payerIncludesSeller =
            termForm.employeeConsiderationPayer === EmployeeConsiderationPayer.PaidBySeller ||
            termForm.employeeConsiderationPayer === EmployeeConsiderationPayer.PaidByBoth;
        const payerIncludesBuyer =
            termForm.employeeConsiderationPayer === EmployeeConsiderationPayer.PaidByBuyer ||
            termForm.employeeConsiderationPayer === EmployeeConsiderationPayer.PaidByBoth;

        if (payerIncludesSeller) {
            if (!isNonNull(cashAtCloser?.considerationForEmployees)) termFormFields.add('cashAtCloserConsiderationForEmployees');
            if (!isNonNull(cashAtCloser?.workingCapitalHoldback?.considerationForEmployees)) termFormFields.add('workingCapitalHoldbackConsiderationForEmployees');

            if (dfpRows.length > 0 && isNonNull(dfpRows[0]?.amount)) {
                if (!isNonNull(dfpRows[0]?.considerationForEmployees)) termFormFields.add('deferredFixedPaymentsConsiderationForEmployees');
            }

            if (dvpEarnout && isNonNull(dvpEarnout.valueAtClosing) && !isNonNull(dvpEarnout.considerationForEmployees)) {
                termFormFields.add('earnoutConsiderationForEmployees');
            }
            if (dvpSynthetic && isNonNull(dvpSynthetic.valueAtClosing) && !isNonNull(dvpSynthetic.considerationForEmployees)) {
                termFormFields.add('syntheticJvEquityConsiderationForEmployees');
            }

            if (isNonNull(summary?.jvEquity?.row?.valueAtClosing) && Number(summary?.jvEquity?.row?.valueAtClosing) !== 0) {
                if (!isNonNull(summary?.jvEquity?.row?.considerationForEmployees)) termFormFields.add('jvEquityConsiderationForEmployees');
            }

            if (corpPreferred && isNonNull(corpPreferred.valueAtClosing) && Number(corpPreferred.valueAtClosing) !== 0) {
                if (!isNonNull(corpPreferred.considerationForEmployees)) termFormFields.add('corporateEquityPreferredConsiderationForEmployees');
            }
            if (corpCommon && isNonNull(corpCommon.valueAtClosing) && Number(corpCommon.valueAtClosing) !== 0) {
                if (!isNonNull(corpCommon.considerationForEmployees)) termFormFields.add('corporateEquityCommonConsiderationForEmployees');
            }

            if (isNonNull(summary?.other)) {
                if (!isNonNull(summary?.otherConsiderationForEmployees)) termFormFields.add('otherConsiderationForEmployees');
            }
        }

        if (payerIncludesBuyer) {
            if (!isNonNull(cashAtCloser?.buyerConsiderationForEmployees)) termFormFields.add('cashAtCloserBuyerConsiderationForEmployees');
            if (!isNonNull(cashAtCloser?.workingCapitalHoldback?.buyerConsiderationForEmployees)) termFormFields.add('workingCapitalHoldbackBuyerConsiderationForEmployees');

            if (dfpRows.length > 0 && isNonNull(dfpRows[0]?.amount)) {
                if (!isNonNull(dfpRows[0]?.buyerConsiderationForEmployees)) termFormFields.add('deferredFixedPaymentsBuyerConsiderationForEmployees');
            }

            if (dvpEarnout && isNonNull(dvpEarnout.valueAtClosing) && !isNonNull(dvpEarnout.buyerConsiderationForEmployees)) {
                termFormFields.add('earnoutBuyerConsiderationForEmployees');
            }
            if (dvpSynthetic && isNonNull(dvpSynthetic.valueAtClosing) && !isNonNull(dvpSynthetic.buyerConsiderationForEmployees)) {
                termFormFields.add('syntheticJvEquityBuyerConsiderationForEmployees');
            }

            if (isNonNull(summary?.jvEquity?.row?.valueAtClosing) && Number(summary?.jvEquity?.row?.valueAtClosing) !== 0) {
                if (!isNonNull(summary?.jvEquity?.row?.buyerConsiderationForEmployees)) termFormFields.add('jvEquityBuyerConsiderationForEmployees');
            }

            if (corpPreferred && isNonNull(corpPreferred.valueAtClosing) && Number(corpPreferred.valueAtClosing) !== 0) {
                if (!isNonNull(corpPreferred.buyerConsiderationForEmployees)) termFormFields.add('corporateEquityPreferredBuyerConsiderationForEmployees');
            }
            if (corpCommon && isNonNull(corpCommon.valueAtClosing) && Number(corpCommon.valueAtClosing) !== 0) {
                if (!isNonNull(corpCommon.buyerConsiderationForEmployees)) termFormFields.add('corporateEquityCommonBuyerConsiderationForEmployees');
            }

            if (isNonNull(summary?.other)) {
                if (!isNonNull(summary?.otherBuyerConsiderationForEmployees)) termFormFields.add('otherBuyerConsiderationForEmployees');
            }
        }

        if (options?.isFsb) {
            if (!isNonNull(cashAtCloser?.fairValue)) termFormFields.add('cashAtCloserFairValue');
            if (!isNonNull(cashAtCloser?.workingCapitalHoldback?.fairValue)) termFormFields.add('workingCapitalHoldbackFairValue');

            if (dfpRows.length > 0 && isNonNull(dfpRows[0]?.amount)) {
                if (!isNonNull(dfpRows[0]?.fairValue)) termFormFields.add('deferredFixedPaymentsFairValue');
            }

            if (dvpEarnout && isNonNull(dvpEarnout.valueAtClosing) && !isNonNull(dvpEarnout.fairValue)) {
                termFormFields.add('earnoutFairValue');
            }
            if (dvpSynthetic && isNonNull(dvpSynthetic.valueAtClosing) && !isNonNull(dvpSynthetic.fairValue)) {
                termFormFields.add('syntheticJvEquityFairValue');
            }

            if (isNonNull(summary?.jvEquity?.row?.valueAtClosing) && Number(summary?.jvEquity?.row?.valueAtClosing) !== 0) {
                if (!isNonNull(summary?.jvEquity?.row?.fairValue)) termFormFields.add('jvEquityFairValue');
            }

            if (corpPreferred && isNonNull(corpPreferred.valueAtClosing) && Number(corpPreferred.valueAtClosing) !== 0) {
                if (!isNonNull(corpPreferred.fairValue)) termFormFields.add('corporateEquityPreferredFairValue');
            }
            if (corpCommon && isNonNull(corpCommon.valueAtClosing) && Number(corpCommon.valueAtClosing) !== 0) {
                if (!isNonNull(corpCommon.fairValue)) termFormFields.add('corporateEquityCommonFairValue');
            }

            if (isNonNull(summary?.other)) {
                if (!isNonNull(summary?.otherFairValue)) termFormFields.add('otherFairValue');
            }
        }
    } else {
        termFormFields.add('loiExpirationDate');
    }

    if (!options?.skipLegalTabs) {
        const sectionById = new Map(formData.legalIssueSections.map((s) => [s.id, s]));

        for (const item of formData.legalIssues) {
            if (!isLegalIssueVisibleByLogic(formData.legalIssues, item)) continue;
            const section = item.sectionId != null ? sectionById.get(item.sectionId) : undefined;
            if (section?.termsFormLinkedField != null && !hasValueForLinkedField(formData.termForm, section.termsFormLinkedField)) continue;
            if (item.showValueInput !== false && !isNonEmpty(item.value)) {
                legalIssueIds.add(item.id);
            }
        }

        const additionalCovenants = formData.additionalCovenants ?? [];
        for (const item of additionalCovenants) {
            if (!isLegalIssueVisibleByLogic(additionalCovenants, item)) continue;
            const section = item.sectionId != null ? sectionById.get(item.sectionId) : undefined;
            if (section?.termsFormLinkedField != null && !hasValueForLinkedField(formData.termForm, section.termsFormLinkedField)) continue;
            if (item.showValueInput !== false && !isNonEmpty(item.value)) {
                additionalCovenantIds.add(item.id);
            }
        }
    }

    return { termFormFields, legalIssueIds, additionalCovenantIds, loiFileRequired };
}

export function hasLoiFormErrors(errors: LoiFormErrors): boolean {
    return errors.termFormFields.size > 0
        || errors.legalIssueIds.size > 0
        || errors.additionalCovenantIds.size > 0
        || !!errors.loiFileRequired;
}
